// TWIN: packages/ui/src/components/FrameLoader.tsx carries the same geometry,
// timing and stroke for the Frame DASHBOARD's loading screen. Deliberately a
// copy, not a shared import: this app has no dependency on @meno/ui at all —
// it is a standalone kiosk PWA that boots on TV hardware over venue wifi, and
// pulling the shared component package in to deduplicate one SVG would hand it
// the whole dependency to save a few hundred bytes. Change one, change both.
//
// Self-drawing Frame mark (stroke-dasharray "draw" animation) — the ONE
// shared "preparing/loading" visual across every such moment: initial
// content fetch (LoadingScreen), post-pairing handoff (PostSetupLoader), and
// post-publish (PublishingLoader). All three used to disagree — some pulsed
// WeTook's mark, some pulsed Frame's — unified here to Frame's own geometry,
// Frame's brand blue (#165CFC — matches apps/frame's --primary and this
// app's own --kiosk-brand token), and ONE size (96px / h-24) matching the
// Android player's equivalent (PreparingThings, 96dp) so the two platforms
// present the same loading moment.
const FRAME_MARK_PATHS = [
  'M82.08 35.3863V22.9163C82.08 17.8463 79.95 14.8063 76.71 14.0163C76.53 13.9663 76.38 13.8763 76.19 13.8463C75.86 13.7963 75.49 13.8363 75.14 13.8263C74.74 13.8163 74.35 13.7663 73.92 13.8163C73.43 13.8663 72.9 14.0463 72.38 14.1763C72.06 14.2663 71.77 14.2963 71.45 14.4063C70.55 14.7263 69.62 15.1463 68.66 15.6963L58.44 21.5963V49.0263L82.08 35.3863Z',
  'M105.72 102.506V36.7063C105.72 31.6363 103.59 28.5963 100.35 27.8063C100.17 27.7563 100.02 27.6663 99.83 27.6363C99.5 27.5863 99.13 27.6263 98.78 27.6163C98.38 27.6063 97.99 27.5563 97.56 27.6063C97.07 27.6563 96.54 27.8363 96.02 27.9663C95.7 28.0563 95.41 28.0863 95.09 28.1963C94.19 28.5163 93.26 28.9363 92.3 29.4863L82.08 35.3863V88.7163C82.08 101.346 80.09 104.316 74.13 107.756L47.28 123.256V135.726C47.28 140.796 49.4 143.826 52.65 144.626C52.83 144.666 52.98 144.766 53.17 144.796C53.5 144.846 53.87 144.806 54.23 144.816C54.63 144.826 55.01 144.876 55.44 144.826C55.94 144.766 56.48 144.596 57.02 144.446C57.32 144.366 57.6 144.346 57.91 144.236C58.81 143.916 59.74 143.496 60.69 142.946L97.77 121.546C103.73 118.106 105.72 115.136 105.72 102.506Z',
  'M47.28 123.256V95.8163L23.64 109.456V121.946C23.64 127.016 25.76 130.046 29.01 130.846C29.19 130.886 29.34 130.986 29.53 131.016C29.86 131.066 30.23 131.026 30.59 131.036C30.99 131.036 31.37 131.086 31.8 131.036C32.3 130.976 32.84 130.806 33.38 130.656C33.68 130.576 33.96 130.556 34.27 130.446C35.17 130.126 36.09 129.706 37.05 129.156L47.28 123.256Z',
  'M58.44 21.5963V9.1263C58.44 4.0563 56.31 1.01631 53.07 0.226309C52.89 0.176309 52.74 0.0863109 52.55 0.0563109C52.22 -0.00368905 51.85 0.0463067 51.5 0.0363067C51.1 0.0263067 50.71 -0.0336878 50.28 0.0263122C49.79 0.0763122 49.26 0.256313 48.74 0.386313C48.42 0.476313 48.13 0.506309 47.81 0.616309C46.91 0.936309 45.98 1.3563 45.02 1.9063L7.94 23.3063C1.99 26.7463 0 29.7163 0 42.3363V108.136C0 113.216 2.12 116.246 5.37 117.046C5.54 117.086 5.7 117.186 5.88 117.216C6.22 117.266 6.59001 117.226 6.95001 117.236C7.35001 117.246 7.72997 117.296 8.15997 117.246C8.65997 117.186 9.19999 117.016 9.73999 116.866C10.04 116.786 10.32 116.756 10.63 116.656C11.52 116.336 12.45 115.916 13.41 115.366L23.64 109.456V56.1263C23.64 43.5063 25.63 40.5363 31.58 37.0963L58.44 21.5963Z',
  'M58.44 21.5963L31.58 37.0963C25.63 40.5363 23.64 43.5063 23.64 56.1263V109.456L47.28 95.8163V123.256L74.13 107.756C80.09 104.316 82.08 101.346 82.08 88.7163V35.3863L58.44 49.0263V21.5963Z',
]

export default function FrameMarkDraw({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 106 145"
      fill="none"
      aria-hidden="true"
      className={`frame-mark-draw h-24 w-auto overflow-visible${className ? ` ${className}` : ''}`}
    >
      {FRAME_MARK_PATHS.map((d, i) => (
        <path key={i} pathLength={1} d={d} />
      ))}
      <style>{`
        .frame-mark-draw path {
          fill: none;
          stroke: #165CFC;
          stroke-width: 3;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
          animation: frame-mark-draw-anim 2.4s ease-in-out infinite;
        }
        @keyframes frame-mark-draw-anim {
          0%   { stroke-dashoffset: 1; }
          45%  { stroke-dashoffset: 0; }
          55%  { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .frame-mark-draw path { animation: none; stroke-dashoffset: 0; }
        }
      `}</style>
    </svg>
  )
}
